import type { ReactNode } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ChecklistIcon from '@mui/icons-material/Checklist';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type { RequirementKind } from '../constants';
import type { DefinitionResponse, SubjectLanguage } from '../types';

import { RequirementDefinitionRow } from './RequirementDefinitionRow';

export interface PatientRequirementsCatalogViewProps {
  definitions: DefinitionResponse[];
  /**
   * Bulk launch for one definition, by id. A render prop so this component
   * stays presentational: it decides WHERE the panel goes, the container
   * decides what it is and whether the viewer may have it.
   */
  renderBulkLaunch?: (definition: DefinitionResponse) => ReactNode;
  /** First load only — this is the one state that may hide the table. */
  isLoading: boolean;
  /**
   * A revalidation behind data already on screen. Shown as a hairline bar, and
   * deliberately NOT as the full-card spinner: every save invalidates the list,
   * so hiding the table here would unmount each row mid-edit and discard the
   * autosave status the admin is watching.
   */
  isRefreshing?: boolean;
  isError: boolean;
  availableKinds: RequirementKind[];
  subject: SubjectLanguage;
  onUpdateDefinition: (
    definitionId: string,
    patch: { label?: string; active?: boolean; default_for_new_patients?: boolean },
  ) => Promise<void>;
  onAddClick: () => void;
  onRetry: () => void;
}

/**
 * Presentational catalog (AC-1). No hooks beyond rendering, so every state —
 * loading, empty, error, all-kinds-taken — is reachable from a story.
 */
export const PatientRequirementsCatalogView = ({
  definitions,
  renderBulkLaunch,
  isLoading,
  isRefreshing = false,
  isError,
  availableKinds,
  subject,
  onUpdateDefinition,
  onAddClick,
  onRetry,
}: PatientRequirementsCatalogViewProps) => {
  const title = `${subject.singularCap} Requirements`;
  const nameColumnHelp = `Shown in texts, emails, and the ${subject.singular}'s checklist.`;

  return (
    <Card>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <ChecklistIcon />
          </Avatar>
        }
        title={title}
        subheader={`What this practice asks ${subject.plural} to provide. Staff see these on the ${subject.singular} record; ${subject.plural} see the labels below.`}
        action={
          availableKinds.length > 0 && !isLoading && !isError ? (
            <Button
              startIcon={<AddIcon />}
              onClick={onAddClick}
              size="small"
              sx={{ mt: 1, mr: 1 }}
            >
              Add requirement
            </Button>
          ) : undefined
        }
      />

      <Box sx={{ height: 2 }}>{isRefreshing && <LinearProgress sx={{ height: 2 }} />}</Box>

      <CardContent sx={{ pt: 0 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && isError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            }
          >
            We could not load the requirement catalog.
          </Alert>
        )}

        {!isLoading && !isError && definitions.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No requirements yet.
          </Typography>
        )}

        {!isLoading && !isError && definitions.length > 0 && (
          <Table size="small" aria-label={title} sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell>Requirement</TableCell>
                {/*
                  The explanation still lives here, once, rather than under
                  every row (GDN-3365) — the column is the right place for a
                  fact true of the whole column. Only its PRESENTATION moved
                  (GDN-3537): as inline caption text it made this header three
                  lines tall while its neighbours were one, so the four header
                  baselines did not line up. It is a tooltip now, so every
                  header is a single line.

                  The icon is focusable and carries the text as its accessible
                  name, so the explanation is reachable by keyboard and read by
                  a screen reader — a hover-only tooltip would have hidden it
                  from exactly the users the GDN-3365 ruling was protecting.
                */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {`Name ${subject.plural} see`}
                    <Tooltip title={nameColumnHelp}>
                      {/*
                        `titleAccess`, not `aria-label`: MUI marks every
                        SvgIcon `aria-hidden` unless it is given one, so an
                        aria-label alone names an element the accessibility
                        tree never reaches. This is what puts the text back in
                        the tree, and `tabIndex` is what lets a keyboard get to
                        it to trigger the tooltip.
                      */}
                      <InfoOutlinedIcon
                        fontSize="inherit"
                        tabIndex={0}
                        titleAccess={nameColumnHelp}
                        sx={{ color: 'text.secondary', cursor: 'help' }}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center">{`New ${subject.plural}`}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {definitions.map(definition => (
                <RequirementDefinitionRow
                  key={definition.patient_requirement_definition_id}
                  definition={definition}
                  subject={subject}
                  bulkLaunch={renderBulkLaunch?.(definition)}
                  onUpdate={patch =>
                    onUpdateDefinition(
                      definition.patient_requirement_definition_id,
                      patch,
                    )
                  }
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
